import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Surface, PageSection } from "@/components/shared";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Search, Percent, Save, Wand2, FolderTree, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";

const PAGE_SIZE = 100;

/**
 * Tax master — per-product tax %. Supports category filter and a
 * "Apply Tax to whole Category" bulk action for fast setup.
 */
export default function Taxes() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [categories, setCategories] = useState([]);
  const [dirty, setDirty] = useState({});
  const [saving, setSaving] = useState(false);
  const [initializing, setInitializing] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkPct, setBulkPct] = useState("3");
  const [bulkSaving, setBulkSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const params = { page, page_size: PAGE_SIZE };
      if (search) params.search = search;
      if (category) params.category = category;
      const r = await api.get("/production/product-taxes", { params });
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
      setTotalPages(r.data.total_pages || 1);
      setDirty({});
    } catch (e) { toast.error(e?.response?.data?.detail || "Load failed"); }
  }, [search, category, page]);
  useEffect(() => { load(); }, [load]);

  // Reset page whenever the filter set changes
  useEffect(() => { setPage(1); }, [search, category]);

  useEffect(() => {
    api.get("/production/product-taxes/categories").then((r) => setCategories(r.data || []));
  }, []);

  const update = (product_id, v) => {
    if (v !== "" && !/^\d*\.?\d*$/.test(v)) return;
    setDirty((d) => ({ ...d, [product_id]: v }));
  };
  const revert = (product_id) => {
    setDirty((d) => { const nd = { ...d }; delete nd[product_id]; return nd; });
  };
  const saveOne = async (product_id) => {
    const v = dirty[product_id];
    const num = Number(v);
    if (Number.isNaN(num) || num < 0 || num > 100) { toast.error("Tax must be between 0 and 100"); return; }
    try {
      await api.put(`/production/product-taxes/${product_id}`, { tax_pct: num });
      toast.success("Tax updated");
      setItems((arr) => arr.map((x) => x.product_id === product_id ? { ...x, tax_pct: num, is_default: false } : x));
      revert(product_id);
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const saveAllDirty = async () => {
    const entries = Object.entries(dirty);
    if (entries.length === 0) return;
    try {
      setSaving(true);
      for (const [pid, v] of entries) {
        const num = Number(v);
        if (Number.isNaN(num) || num < 0 || num > 100) continue;
        await api.put(`/production/product-taxes/${pid}`, { tax_pct: num });
      }
      toast.success(`Updated ${entries.length} product(s)`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const bulkInit = async () => {
    if (!window.confirm("Set 3% tax for every product that doesn't have a tax configured yet? This is safe to re-run.")) return;
    try {
      setInitializing(true);
      const r = await api.post("/production/product-taxes/bulk-init", null, { params: { default_pct: 3 } });
      toast.success(`Initialised ${r.data.initialized} products at 3% · ${r.data.already_configured} already configured`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Init failed"); }
    finally { setInitializing(false); }
  };

  const bulkSetCategory = async () => {
    const num = Number(bulkPct);
    if (Number.isNaN(num) || num < 0 || num > 100) { toast.error("Tax must be between 0 and 100"); return; }
    try {
      setBulkSaving(true);
      const r = await api.post("/production/product-taxes/bulk-set-by-category", null, {
        params: { category, tax_pct: num },
      });
      toast.success(`Applied ${num}% to ${r.data.updated} product(s) in "${category}"`);
      setBulkOpen(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBulkSaving(false); }
  };

  const dirtyCount = Object.keys(dirty).length;
  const defaultCount = items.filter((x) => x.is_default).length;

  return (
    <div className="space-y-4">
      <PageSection
        title="Product Taxes"
        description="Per-product tax % used later by the Sales / Warehouse Dispatch module. Default rate is 3%."
        action={
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={bulkInit} disabled={initializing} className="gap-1.5" data-testid="tax-bulk-init">
              <Wand2 className="h-4 w-4" /> {initializing ? "Setting…" : "Set All to 3%"}
            </Button>
            {dirtyCount > 0 && (
              <Button className="bg-[#9B1C1C] hover:bg-[#7F1D1D] gap-1.5" onClick={saveAllDirty} disabled={saving} data-testid="tax-save-all">
                <Save className="h-4 w-4" /> {saving ? "Saving…" : `Save ${dirtyCount} Changes`}
              </Button>
            )}
          </div>
        }
      >
        <Surface padded={false}>
          <div className="p-3 border-b border-gray-100 flex items-center gap-3 flex-wrap">
            <div className="relative flex-1 max-w-sm min-w-[220px]">
              <Search className="h-4 w-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search product name / SKU..." className="pl-8" data-testid="tax-search" />
            </div>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="h-9 rounded-md border border-gray-200 text-sm px-2 min-w-[200px]"
              data-testid="tax-category-filter"
            >
              <option value="">All categories</option>
              {categories.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            {category && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => { setBulkPct("3"); setBulkOpen(true); }}
                className="gap-1.5 border-blue-200 text-blue-700 hover:bg-blue-50"
                data-testid="tax-bulk-category-btn"
              >
                <FolderTree className="h-4 w-4" /> Apply Tax to whole &ldquo;{category}&rdquo;
              </Button>
            )}
            <div className="ml-auto flex items-center gap-3 text-xs text-gray-500">
              <span data-testid="tax-total-count">
                {total.toLocaleString()} product{total !== 1 ? "s" : ""}
                {items.length !== total && (
                  <> · showing {(page - 1) * PAGE_SIZE + 1}-{Math.min(page * PAGE_SIZE, total)}</>
                )}
              </span>
              {defaultCount > 0 && <span className="text-amber-700">· {defaultCount} on default 3%</span>}
            </div>
          </div>
          <div className="max-h-[70vh] overflow-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 sticky top-0">
                <tr className="text-left text-[11px] uppercase tracking-widest text-gray-500">
                  <th className="px-3 py-2">Product</th>
                  <th className="px-3 py-2">SKU</th>
                  <th className="px-3 py-2">Category</th>
                  <th className="px-3 py-2 text-right w-40">Tax %</th>
                  <th className="px-3 py-2 w-24"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {items.length === 0 && (
                  <tr><td colSpan={5} className="px-3 py-10 text-center text-gray-400">
                    <Percent className="h-8 w-8 mx-auto mb-2 opacity-30" /> No products match.
                  </td></tr>
                )}
                {items.map((row) => {
                  const isDirty = dirty[row.product_id] !== undefined;
                  const display = isDirty ? dirty[row.product_id] : String(row.tax_pct);
                  return (
                    <tr key={row.product_id} className={`hover:bg-gray-50 ${isDirty ? "bg-amber-50" : ""}`} data-testid={`tax-row-${row.product_id}`}>
                      <td className="px-3 py-2 font-medium">{row.product_name}</td>
                      <td className="px-3 py-2 font-mono text-xs text-gray-600">{row.product_sku || "—"}</td>
                      <td className="px-3 py-2 text-xs text-gray-600">{row.category || "—"}</td>
                      <td className="px-3 py-2">
                        <div className="flex items-center justify-end gap-1">
                          <Input
                            value={display}
                            onChange={(e) => update(row.product_id, e.target.value)}
                            className="w-24 text-right tabular-nums h-8"
                            data-testid={`tax-input-${row.product_id}`}
                          />
                          <span className="text-gray-500 text-xs">%</span>
                          {row.is_default && !isDirty && <span className="text-[10px] text-amber-700 font-semibold uppercase tracking-widest ml-1" title="Using default rate">DEF</span>}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-right">
                        {isDirty && (
                          <div className="flex items-center justify-end gap-1">
                            <Button size="sm" className="h-7 px-2 text-xs bg-emerald-600 hover:bg-emerald-700" onClick={() => saveOne(row.product_id)} data-testid={`tax-save-${row.product_id}`}>
                              Save
                            </Button>
                            <button onClick={() => revert(row.product_id)} className="text-xs text-gray-500 hover:text-gray-800 px-1" data-testid={`tax-revert-${row.product_id}`}>×</button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Surface>

        <TaxPaginationBar page={page} totalPages={totalPages} total={total} setPage={setPage} />
      </PageSection>

      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent className="max-w-md" data-testid="tax-bulk-dialog">
          <DialogHeader>
            <DialogTitle>Apply Tax to Category</DialogTitle>
            <DialogDescription>
              Set the same tax % for every product in <span className="font-semibold">&ldquo;{category}&rdquo;</span>.
              This will overwrite any product-level values already set in this category.
            </DialogDescription>
          </DialogHeader>
          <div>
            <label className="text-[11px] uppercase tracking-widest text-gray-500 block mb-1">Tax %</label>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                step="0.01"
                min="0"
                max="100"
                value={bulkPct}
                onChange={(e) => setBulkPct(e.target.value)}
                className="w-32 text-right tabular-nums"
                data-testid="tax-bulk-pct"
                autoFocus
              />
              <span className="text-gray-500 text-sm">%</span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkOpen(false)}>Cancel</Button>
            <Button className="bg-[#9B1C1C] hover:bg-[#7F1D1D]" onClick={bulkSetCategory} disabled={bulkSaving} data-testid="tax-bulk-apply">
              {bulkSaving ? "Applying…" : `Apply ${bulkPct || 0}% to Category`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TaxPaginationBar({ page, totalPages, total, setPage }) {
  if (totalPages <= 1) return null;
  const canPrev = page > 1;
  const canNext = page < totalPages;
  return (
    <div className="bg-white border border-gray-200 rounded-md px-4 py-2.5 flex items-center gap-3 text-xs text-gray-600" data-testid="tax-pagination">
      <span data-testid="tax-pagination-info">
        Page <span className="font-semibold text-gray-900">{page}</span> of {totalPages.toLocaleString()} · {total.toLocaleString()} products
      </span>
      <div className="ml-auto flex items-center gap-1">
        <Button size="sm" variant="outline" className="h-8 px-2" disabled={!canPrev} onClick={() => setPage(1)} data-testid="tax-pagination-first">« First</Button>
        <Button size="sm" variant="outline" className="h-8 px-2" disabled={!canPrev} onClick={() => setPage(page - 1)} data-testid="tax-pagination-prev">
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <Button size="sm" variant="outline" className="h-8 px-2" disabled={!canNext} onClick={() => setPage(page + 1)} data-testid="tax-pagination-next">
          <ChevronRight className="h-4 w-4" />
        </Button>
        <Button size="sm" variant="outline" className="h-8 px-2" disabled={!canNext} onClick={() => setPage(totalPages)} data-testid="tax-pagination-last">Last »</Button>
      </div>
    </div>
  );
}
